import { useEffect, useState } from 'react'
import { useUserStore } from '@/state/user-store'
import { fetchUser, uploadUser } from '@/state/user-actions'
import { Button } from '@/components/ui/button'
import { useSessionContext } from '@/state/session-context'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { toast, Toaster } from 'react-hot-toast'
import { NotificationSettings } from '@/components/notification-settings'
import { twUserColors } from '../../../../shared-src/color'
import Modal from '@/components/modal'
import Faq from '../popups/faq'
import Terms from '../popups/terms'
import Privacy from '../popups/privacy'
import Contact from '../popups/contact'
import { SettingsSection } from './settings-section'
import { AccountSection } from './account-section'
import { ProfileSection } from './profile-section'
import { SubscriptionSection } from './subscription-section'
import * as env from '@/env'


interface SettingsProps {
	onNotificationChange?: () => void
}

export default function Settings({ onNotificationChange }: SettingsProps = {}) {
	const navigate = useNavigate()
	const { session, logout } = useSessionContext()
	const user = useUserStore(state => state.user)
	const [searchParams] = useSearchParams()

	const [name, setName] = useState(user?.name)
	const [introduction, setIntroduction] = useState(user?.introduction)
	const [color, setColor] = useState(user?.color || twUserColors[0])
	const [location, setLocation] = useState(user?.location || { lat: 43.7, lng: -79.4 })
	const [isSaving, setIsSaving] = useState(false)
	//	snapshot of the form values at the moment they were seeded from the loaded user.
	//	dirty = current form values differ from this baseline. captures the same fallbacks
	//	used during seeding (eg. twUserColors[0] when user.color is unset), so that
	//	users who never picked a color don't see a false-positive "unsaved changes".
	type Baseline = { name?: string; introduction?: string; color: string; location: { lat: number; lng: number } }
	const [baseline, setBaseline] = useState<Baseline | null>(null)

	const validSections = ['profile', 'notifications', 'subscription', 'account']
	const sectionParam = searchParams.get('section')
	const initialSection = sectionParam && validSections.includes(sectionParam) ? sectionParam : null

	const [expandedSection, setExpandedSection] = useState<string | null>(initialSection)

	const [faqOpen, setFaqOpen] = useState(false)
	const [termsOpen, setTermsOpen] = useState(false)
	const [privacyOpen, setPrivacyOpen] = useState(false)
	const [contactOpen, setContactOpen] = useState(false)

	const profileHasUnsavedChanges = !!baseline && (
		name !== baseline.name
		|| introduction !== baseline.introduction
		|| color !== baseline.color
		|| location.lat !== baseline.location.lat
		|| location.lng !== baseline.location.lng
	)

	useEffect(() => {
		const fetchUser_ = async () => {
			if (!session?.userId) return
			try {
				const user = await fetchUser(session.userId)
				const initialName = user?.name
				const initialIntroduction = user?.introduction
				const initialColor = user?.color || twUserColors[0]
				const initialLocation = user?.location || { lat: 43.7, lng: -79.4 }
				setName(initialName)
				setIntroduction(initialIntroduction)
				setColor(initialColor)
				setLocation(initialLocation)
				setBaseline({ name: initialName, introduction: initialIntroduction, color: initialColor, location: initialLocation })
			} catch (err) {
				//	transient failure — leave the form unseeded so the "Loading..." fallback below stays.
				//	the user can reload to retry. matches the pattern in chat.tsx.
				console.warn('Transient user fetch in settings; leaving form unseeded:', err)
			}
		}
		fetchUser_()
	}, [session?.userId])

	const handleLogout = async () => {
		try {
			await logout()
			navigate('/')
		} catch (err) {
			console.error('Logout failed:', err)
		}
	}

	const handleBack = () => {
		if (profileHasUnsavedChanges) {
			if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
				navigate('/chat')
			}
		} else {
			navigate('/chat')
		}
	}

	const handleSaveProfile = async () => {
		if (!user) return

		const trimmedName = name?.trim()
		if (!trimmedName) {
			toast.error('Name cannot be empty')
			return
		}
		if (trimmedName.length > 20) {
			toast.error('Name must be 20 characters or less')
			return
		}

		setIsSaving(true)
		try {
			user.name = trimmedName
			user.introduction = introduction
			user.color = color
			user.location = location

			await uploadUser()
			//	reset baseline to the values we just persisted, so the dirty check
			//	turns the Save button back to disabled. captures values as of the
			//	save start, not current form state: if the user edited during the
			//	in-flight save, the new edit remains dirty relative to this baseline.
			setBaseline({ name, introduction, color, location })
			toast.success('Profile saved successfully')
		} catch (err) {
			console.error('Failed to save profile:', err)
			toast.error('Failed to save profile')
		} finally {
			setIsSaving(false)
		}
	}

	const toggleSection = (section: string) => {
		setExpandedSection(expandedSection === section ? null : section)
	}

	//	gate on baseline being set, not just `user`. `fetchUser` inside the useEffect updates
	//	the zustand store BEFORE the local setState calls run; that store update causes a
	//	synchronous re-render in which `user` is populated but `location`/`baseline` are still
	//	their initial-useState defaults. rendering LocationPicker in that intermediate frame
	//	means react-leaflet's MapContainer binds its whenReady handler to that first-render
	//	mapDidLoad — whose closure holds `props.location = default`. when the map finishes
	//	async init, that stale closure calls `props.setLocation(default)`, overwriting the
	//	just-seeded real location and leaving the form permanently dirty against baseline.
	//	ha-296. also consistent with ha-270's stated intent that a transient fetch failure
	//	should leave the "Loading..." fallback showing until the user reloads.
	if (!user || !baseline)
		return <div>Loading...</div>

	return (
		<div className="container mx-auto p-4 max-w-2xl">
			<div className="flex items-center gap-4 mb-6">
				<Button
					onClick={handleBack}
					variant="ghost"
					size="icon"
					className="h-8 w-8"
				>
					<ChevronLeft className="h-5 w-5" />
				</Button>
				<h1 className="text-2xl font-bold">Settings</h1>
			</div>

			<div className="flex gap-2 mb-6 flex-wrap">
				<Button
					variant="link"
					className="text-rose-600 hover:text-rose-600/80"
					onClick={() => setFaqOpen(true)}
				>
					FAQ
				</Button>
				<Button
					variant="link"
					className="text-rose-600 hover:text-rose-600/80"
					onClick={() => setTermsOpen(true)}
				>
					Terms
				</Button>
				<Button
					variant="link"
					className="text-rose-600 hover:text-rose-600/80"
					onClick={() => setPrivacyOpen(true)}
				>
					Privacy
				</Button>
				<Button
					variant="link"
					className="text-rose-600 hover:text-rose-600/80"
					onClick={() => setContactOpen(true)}
				>
					Contact
				</Button>
			</div>

			<div className="space-y-3">
				<SettingsSection
					title="Profile"
					isExpanded={expandedSection === 'profile'}
					onToggle={() => toggleSection('profile')}
					hasUnsavedChanges={profileHasUnsavedChanges}
				>
					<ProfileSection
						name={name}
						onNameChange={setName}
						color={color}
						onColorChange={setColor}
						location={location}
						onLocationChange={setLocation}
						introduction={introduction}
						onIntroductionChange={setIntroduction}
						isSaving={isSaving}
						hasUnsavedChanges={profileHasUnsavedChanges}
						onSave={handleSaveProfile}
					/>
				</SettingsSection>

				<SettingsSection
					title="Notifications"
					isExpanded={expandedSection === 'notifications'}
					onToggle={() => toggleSection('notifications')}
				>
					<NotificationSettings onNotificationChange={onNotificationChange} />
				</SettingsSection>

				{/* Subscription section hidden for beta launch
				{!env.isProduction && (
					<SettingsSection
						title="Subscription"
						isExpanded={expandedSection === 'subscription'}
						onToggle={() => toggleSection('subscription')}
					>
						<SubscriptionSection />
					</SettingsSection>
				)}
				*/}

				<SettingsSection
					title="Account"
					isExpanded={expandedSection === 'account'}
					onToggle={() => toggleSection('account')}
				>
					<AccountSection
						userId={user.userId}
						email={user.email}
						onLogout={handleLogout}
					/>
				</SettingsSection>
			</div>

			<Modal open={faqOpen} onOpenChange={setFaqOpen} title="FAQ">
				<Faq />
			</Modal>

			<Modal open={termsOpen} onOpenChange={setTermsOpen} title="Terms of Service">
				<Terms />
			</Modal>

			<Modal open={privacyOpen} onOpenChange={setPrivacyOpen} title="Privacy Policy">
				<Privacy />
			</Modal>

			<Modal open={contactOpen} onOpenChange={setContactOpen} title="Contact">
				<Contact prefillEmail={session?.email} />
			</Modal>

			<Toaster />
		</div>
	)
} 